import { useEffect, useState } from "preact/hooks";
import { apiClient, getDebugReportEndpoint } from "../../constants.ts";
import { useAuth } from "./db.ts";
import { Button, PageSpinner } from "./components.tsx";
import { usePageMeta } from "./pageMeta.ts";
import { toast } from "react-hot-toast";

type JsonValue =
  | string
  | number
  | boolean
  | null
  | { [key: string]: JsonValue }
  | JsonValue[];

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; report: JsonValue }
  | { kind: "denied" };

const plzGetAdminText = "plz get an admin";

const getDebugId = () =>
  new URLSearchParams(globalThis.location.search).get("id")?.trim() ?? "";

const isPermittedError = (error: string) =>
  error === "not-permitted" || error === "not-found";

const previewValue = (value: JsonValue): string => {
  if (value === null) return "null";
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (typeof value === "object") return `Object(${Object.keys(value).length})`;
  if (typeof value === "string") {
    const preview = value.length > 48 ? `${value.slice(0, 48)}...` : value;
    return `"${preview}"`;
  }
  return String(value);
};

const toJsonValue = (value: unknown): JsonValue =>
  JSON.parse(JSON.stringify(value));

const stringifyJson = (value: JsonValue) => JSON.stringify(value, null, 2);

const scalarClassName = (value: JsonValue) =>
  value === null
    ? "text-orange-500 dark:text-orange-300"
    : typeof value === "string"
    ? "text-emerald-600 dark:text-emerald-300"
    : typeof value === "number"
    ? "text-cyan-700 dark:text-cyan-300"
    : typeof value === "boolean"
    ? "text-fuchsia-700 dark:text-fuchsia-300"
    : "text-text-primary-light dark:text-text-primary";

const renderScalar = (value: JsonValue) => (
  <span className={scalarClassName(value)}>
    {typeof value === "string" ? `"${value}"` : String(value)}
  </span>
);

const shouldNodeBeOpen = (
  depth: number,
  expandMode: "auto" | "all-open" | "all-closed",
) => {
  if (expandMode === "all-open") return true;
  if (expandMode === "all-closed") return false;
  return depth <= 1;
};

const JsonTreeNode = (
  {
    label,
    value,
    depth,
    expandMode,
    onCopy,
  }: {
    label?: string;
    value: JsonValue;
    depth: number;
    expandMode: "auto" | "all-open" | "all-closed";
    onCopy: (value: JsonValue, successMessage: string) => Promise<void>;
  },
) => {
  if (value === null || typeof value !== "object") {
    return (
      <div className="font-mono text-xs leading-6 rounded px-2 py-1 hover:bg-surface-overlay-light dark:hover:bg-surface-overlay flex items-center justify-between gap-2">
        <div className="min-w-0 break-all">
          {label && (
            <span className="text-blue-700 dark:text-blue-300">
              "{label}":{" "}
            </span>
          )}
          {renderScalar(value)}
        </div>
        <button
          type="button"
          className="text-[10px] px-2 py-0.5 rounded border border-border-subtle-light dark:border-border-subtle text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary"
          onClick={() =>
            onCopy(value, label ? `Copied ${label}` : "Copied value")}
        >
          Copy
        </button>
      </div>
    );
  }

  const entries = Array.isArray(value)
    ? value.map((item, index) => [String(index), item] as const)
    : Object.entries(value);

  return (
    <details
      className="rounded-md border border-border-subtle-light dark:border-border-subtle bg-surface-overlay-light dark:bg-surface-overlay"
      open={shouldNodeBeOpen(depth, expandMode)}
    >
      <summary className="cursor-pointer select-none px-3 py-2 font-mono text-xs text-text-primary-light dark:text-text-primary flex items-center justify-between gap-2">
        <span>
          {label && (
            <span className="text-blue-700 dark:text-blue-300">"{label}":</span>
          )}
          <span className="text-violet-700 dark:text-violet-300">
            {Array.isArray(value) ? "[" : "{"}
          </span>
          <span className="text-text-secondary-light dark:text-text-secondary">
            {previewValue(value)}
          </span>
          <span className="text-violet-700 dark:text-violet-300">
            {Array.isArray(value) ? "]" : "}"}
          </span>
        </span>
        <button
          type="button"
          className="text-[10px] px-2 py-0.5 rounded border border-border-subtle-light dark:border-border-subtle text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onCopy(value, label ? `Copied ${label}` : "Copied section");
          }}
        >
          Copy
        </button>
      </summary>
      <div className="pl-4 pr-2 pb-2 flex flex-col gap-1">
        {entries.map(([key, child]) => (
          <JsonTreeNode
            key={key}
            label={key}
            value={child}
            depth={depth + 1}
            expandMode={expandMode}
            onCopy={onCopy}
          />
        ))}
      </div>
    </details>
  );
};

export const DebugReportPage = () => {
  usePageMeta({
    title: "Debug Report | prompt2bot",
    description: "Generate a debug report for prompt2bot support.",
  });
  const { user } = useAuth();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [treeRefreshKey, setTreeRefreshKey] = useState(0);
  const [expandMode, setExpandMode] = useState<
    "auto" | "all-open" | "all-closed"
  >("auto");

  useEffect(() => {
    const token = user?.refresh_token ?? "";
    const id = getDebugId();
    if (!token || !id) {
      setState({ kind: "denied" });
      return;
    }
    apiClient({
      endpoint: getDebugReportEndpoint,
      token,
      payload: { id },
    }).then((result) => {
      if (!result.success && isPermittedError(result.error)) {
        setState({ kind: "denied" });
        return;
      }
      if (!result.success) {
        setState({ kind: "denied" });
        return;
      }
      setState({ kind: "ready", report: toJsonValue(result.report) });
    }).catch(() => {
      setState({ kind: "denied" });
    });
  }, [user?.refresh_token]);

  if (state.kind === "loading") return <PageSpinner />;

  if (state.kind === "denied") {
    return (
      <div className="max-w-3xl mx-auto p-6 rounded-xl bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle font-display text-xl">
        {plzGetAdminText}
      </div>
    );
  }

  const treeData = state.report;
  const prettyJson = stringifyJson(treeData);
  const copyText = async (text: string, successMessage: string) => {
    await navigator.clipboard.writeText(text);
    toast.success(successMessage);
  };
  const copyJson = (value: JsonValue, successMessage: string) =>
    copyText(stringifyJson(value), successMessage);

  return (
    <div className="max-w-5xl mx-auto w-full flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <Button
          className="px-3 py-1.5 text-xs"
          onClick={() => copyText(prettyJson, "Copied JSON")}
        >
          Copy JSON
        </Button>
        <Button
          className="px-3 py-1.5 text-xs"
          onClick={() => {
            setExpandMode("all-open");
            setTreeRefreshKey((x) => x + 1);
          }}
        >
          Expand All
        </Button>
        <Button
          className="px-3 py-1.5 text-xs"
          onClick={() => {
            setExpandMode("all-closed");
            setTreeRefreshKey((x) => x + 1);
          }}
        >
          Collapse All
        </Button>
      </div>
      <div
        key={treeRefreshKey}
        className="w-full rounded-xl p-3 bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle"
      >
        <JsonTreeNode
          value={treeData}
          depth={0}
          expandMode={expandMode}
          onCopy={copyJson}
        />
      </div>
    </div>
  );
};
